import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Button,
  Paper,
  Divider,
  CircularProgress,
  Alert,
  Chip,
  AppBar,
  Toolbar,
  Container,
  IconButton,
  Badge,
  Tooltip,
  Snackbar,
  Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow,
} from '@mui/material';
import LayoutKasir from './layoutKasir';
import {
  CheckCircle as CheckCircleIcon,
  AccessTime as AccessTimeIcon,
 Notifications as NotificationsIcon,
  Bluetooth as BluetoothIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import useSWR from 'swr';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';
import { id } from 'date-fns/locale';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import { Logout, reset } from '../../fitur/AuthSlice';
import ThermalPrinter from './ThermalPrinter';
import { apiHost } from '../../utils/apiHost';
import { ambilKonfigStruk, barisStrukKasir } from '../../struk/strukWeb';
import { cetakBrowser } from '../../struk/cetakStruk';

const getApiBaseUrl = () => {
  const protocol = window.location.protocol === "https:" ? "https" : "http";
  const baseUrl = apiHost().replace(/^https?:\/\//, "");
  return `${protocol}://${baseUrl}`;
};

const fetcher = async (url) => {
  const response = await axios.get(url, {
    withCredentials: true
  });
  return response.data;
};

export const Notification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentView, setCurrentView] = useState('notification');
  const [branchName, setBranchName] = useState("Nama Toko");
  const [showPrinterSettings, setShowPrinterSettings] = useState(false);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerInstance, setPrinterInstance] = useState(null);
  const [notificationSnackbar, setNotificationSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  useEffect(() => {
    const fetchBranchName = async () => {
      try {
        const response = await axios.get(`${getApiBaseUrl()}/me`, { withCredentials: true });
        setBranchName(response.data.cabang?.namacabang || "Nama Toko Tidak Diketahui");
      } catch (err) {
        console.error("Gagal mendapatkan nama cabang:", err);
        setBranchName("Nama Toko Tidak Diketahui");
      }
    };
    fetchBranchName();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    if (path === '/produkpercabang') {
      setCurrentView('home');
    } else if (path === '/transaksikasir') {
      setCurrentView('history');
    } else if (path === '/notification' || path === '/notification-kasir') {
      setCurrentView('notification');
    } else if (path === '/detail' || path === '/detail-kasir') {
      setCurrentView('detail');
    } else if (path === '/profilekasir') {
      setCurrentView('profile');
    }
  }, [location.pathname]);

  const handleNavigate = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    try {
      dispatch(reset());
      dispatch(Logout());
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/');
    }
  };

  const handleNotificationClick = () => {
    navigate('/notification-kasir');
  };

  const { data: notificationData, error: notificationError, mutate: mutateNotifications } = useSWR(
    `${getApiBaseUrl()}/notifications`,
    fetcher,
    { 
      refreshInterval: 10000,
      revalidateOnFocus: true
    }
  );
  
  const notificationCount = notificationData?.data?.length || 0;
  
  const { data: pendingOrdersData, error: pendingOrdersError, mutate: mutatePendingOrders } = useSWR(
    `${getApiBaseUrl()}/getpendingorder`,
    fetcher,
    { 
      refreshInterval: 10000,
      revalidateOnFocus: true
    }
  );
  
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: id });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const printReceipt = (order) => {
    if (!order || !order.transaksiUuid) {
      console.error("Invalid order data:", order);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Cannot print receipt: Invalid order data'
      });
      return;
    }
    
    // Nota pesanan yang baru diterima: tata letak SAMA dengan struk kasir (struk/strukLayout.js, juga dipakai Flutter).
    (async () => {
      const { cabangConfig, logoUrl } = await ambilKonfigStruk(axios, getApiBaseUrl(), user?.cabang?.uuid);
      const receipt = {
        items: (order.transaksi_details || []).map((d) => ({
          name: d.barang?.namabarang || 'Item',
          price: Number(d.harga_satuan) || 0,
          quantity: Number(d.jumlah) || 0,
        })),
        total: Number(order.total_harga) || 0,
        paymentMethod: order.status_pembayaran === 'settlement' ? 'Paid' : 'Pending',
        namaCustomer: order.namaCustomer || 'Walk-in Customer',
        createdAt: order.createdAt || Date.now(),
        orderId: typeof order.transaksiUuid === 'object' ? order.transaksiUuid.order_id : order.order_id || '-',
        meja: order.table?.nama_meja || '',
      };
      cetakBrowser(barisStrukKasir({
        receipt,
        cabangConfig,
        namaCabang: user?.cabang?.namacabang || 'Kasir',
        kasir: user?.nama || 'Kasir',
        logoUrl,
      }));
    })();
  };

  const markAsRead = async (notificationIds) => {
    try {
      setLoading(true);
      await axios.post(
        `${getApiBaseUrl()}/notifications/read`,
        { notificationIds },
        { withCredentials: true }
      );
      mutateNotifications();
      setLoading(false);
    } catch (err) {
      setError('Failed to mark notifications as read.');
      setLoading(false);
      console.error(err);
    }
  };
  
  const acceptOrder = async (transaksiUuid) => {
    try {
      setLoading(true);
      const response = await axios.post(`${getApiBaseUrl()}/acceptorder`, { transaksiUuid }, { withCredentials: true });
      
      const orderData = response.data?.order;
      if (!orderData) {
        throw new Error("Server response missing order data");
      }
      
      mutatePendingOrders();
      setLoading(false);

      Swal.fire({
        icon: 'success',
        title: 'Order Accepted',
      }).then((result) => {
        if (result.isConfirmed) {
          printReceipt(orderData);
        }
      });
    } catch (err) {
      setLoading(false);
      Swal.fire({ 
        icon: 'success', 
        title: 'Order Accepted',
      });
    }
  };

  const markAllAsRead = async () => {
    if (!notificationData?.data || notificationData.data.length === 0) return;
    
    const notificationIds = notificationData.data.map(notification => notification.id);
    await markAsRead(notificationIds);
  };
  
  if (!notificationData || !pendingOrdersData) {
    return (
      <LayoutKasir
        currentView={currentView}
        onViewChange={setCurrentView}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        showPrinterSettings={showPrinterSettings}
        onShowPrinterSettings={setShowPrinterSettings}
        printerConnected={printerConnected}
        printerInstance={printerInstance}
        onSetPrinterInstance={setPrinterInstance}
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <CircularProgress />
        </Box>
      </LayoutKasir>
    );
  }

  if (notificationError || pendingOrdersError) {
    return (
      <LayoutKasir
        currentView={currentView}
        onViewChange={setCurrentView}
        onNavigate={handleNavigate}
        onLogout={handleLogout}
        showPrinterSettings={showPrinterSettings}
        onShowPrinterSettings={setShowPrinterSettings}
        printerConnected={printerConnected}
        printerInstance={printerInstance}
        onSetPrinterInstance={setPrinterInstance}
      >
        <Box sx={{ p: 3 }}>
          <Alert severity="error">
            Failed to load notifications. Please try again later.
          </Alert>
        </Box>
      </LayoutKasir>
    );
  }

  return (
    <LayoutKasir
      currentView={currentView}
      onViewChange={setCurrentView}
      onNavigate={handleNavigate}
      onLogout={handleLogout}
      showPrinterSettings={showPrinterSettings}
      onShowPrinterSettings={setShowPrinterSettings}
      printerConnected={printerConnected}
      printerInstance={printerInstance}
      onSetPrinterInstance={setPrinterInstance}
    >
      <Box sx={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
        <ThermalPrinter 
          open={showPrinterSettings} 
          onClose={() => setShowPrinterSettings(false)}
          onPrinterConnected={(device) => {
            setPrinterInstance(device);
            setPrinterConnected(true);
          }}
          onPrinterDisconnected={() => {
            setPrinterInstance(null);
            setPrinterConnected(false);
          }}
          currentPrinter={printerInstance}
          connectionStatus={printerInstance?.getConnectionStatus?.()}
        />

       
    

      <Container maxWidth="lg" sx={{ mt: 3, mb: 3, width: '100%' }}>
  {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '12px' }}>{error}</Alert>}

  {/* ── TABEL PENDING ORDERS ── */}
  <Paper elevation={0} variant="outlined" sx={{ mb: 3, borderRadius: '12px', overflow: 'hidden' }}>
    <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
      <AccessTimeIcon sx={{ fontSize: 18, color: 'warning.main' }} />
      <Typography variant="subtitle2" fontWeight={600}>
        Pesanan Pending
      </Typography>
      <Chip label={pendingOrdersData?.data?.length || 0} size="small" color="warning" sx={{ height: 20, fontSize: 11 }} />
    </Box>

    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: 'grey.50' } }}>
            <TableCell>No. Order</TableCell>
            <TableCell>Meja</TableCell>
            <TableCell>Item Pesanan</TableCell>
            <TableCell>Total</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Waktu</TableCell>
            <TableCell align="right">Aksi</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {pendingOrdersData?.data?.length > 0 ? (
            pendingOrdersData.data.map((order) => (
              <TableRow key={order.uuid} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    #{order.order_id || 'na'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip label={order.table?.nama_meja || 'No Table'} size="small" color="info" variant="outlined" sx={{ fontSize: 11 }} />
                </TableCell>
                <TableCell>
                  {order.transaksi_details?.map((item, idx) => (
                    <Typography key={idx} variant="caption" display="block" color="text.secondary">
                      {item.jumlah}× {item.barang?.namabarang} – Rp{item.harga_satuan?.toLocaleString()}
                    </Typography>
                  ))}
                </TableCell>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    Rp{order.total_harga?.toLocaleString()}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={order.status_pembayaran === 'settlement' ? 'Lunas' : 'Pending'}
                    size="small"
                    color={order.status_pembayaran === 'settlement' ? 'success' : 'warning'}
                    sx={{ fontSize: 11 }}
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(order.createdAt)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Button
                    variant="contained"
                    size="small"
                    disabled={loading}
                    onClick={() => acceptOrder(order.uuid)}
                    startIcon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                    sx={{ fontSize: 11, borderRadius: '8px', bgcolor: '#337ab7', '&:hover': { bgcolor: '#286090' } }}
                  >
                    Terima
                  </Button>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary', fontSize: 13 }}>
                Tidak ada pesanan pending
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>

  {/* ── TABEL NOTIFICATIONS ── */}
  <Paper elevation={0} variant="outlined" sx={{ borderRadius: '12px', overflow: 'hidden' }}>
    <Box sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <NotificationsIcon sx={{ fontSize: 18, color: 'primary.main' }} />
        <Typography variant="subtitle2" fontWeight={600}>Notifikasi</Typography>
        <Chip label={notificationData?.data?.length || 0} size="small" color="primary" sx={{ height: 20, fontSize: 11 }} />
      </Box>
      <Button size="small" onClick={markAllAsRead} disabled={loading} sx={{ fontSize: 11 }}>
        Tandai semua dibaca
      </Button>
    </Box>

    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow sx={{ '& th': { fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.04em', bgcolor: 'grey.50' } }}>
            <TableCell>Pesan</TableCell>
            <TableCell>Transaksi</TableCell>
            <TableCell>Waktu</TableCell>
            <TableCell align="right">Aksi</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {notificationData?.data?.length > 0 ? (
            notificationData.data.map((notification) => (
              <TableRow key={notification.id} hover>
                <TableCell>
                  <Typography variant="body2">{notification.message}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {notification.transaksiUuid
                      ? typeof notification.transaksiUuid === 'object'
                        ? notification.transaksiUuid.order_id
                        : notification.transaksiUuid
                      : '–'}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(notification.timestamp)}
                  </Typography>
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'flex-end' }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => markAsRead([notification.id])}
                      sx={{ fontSize: 11, borderRadius: '8px', minWidth: 0 }}
                    >
                      Baca
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => {
                        markAsRead([notification.id]);
                        if (notification.transaksiUuid) {
                          acceptOrder(notification.transaksiUuid);
                        } else {
                          Swal.fire({ icon: 'error', title: 'Error', text: 'Transaksi tidak ditemukan.' });
                        }
                      }}
                      sx={{ fontSize: 11, borderRadius: '8px', bgcolor: '#337ab7', '&:hover': { bgcolor: '#286090' } }}
                    >
                      Terima
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary', fontSize: 13 }}>
                Tidak ada notifikasi
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </Paper>

  {loading && (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
      <CircularProgress size={16} />
      <Typography variant="caption" color="text.secondary">Memperbarui...</Typography>
    </Box>
  )}
</Container>

        <Snackbar
          open={notificationSnackbar.open}
          autoHideDuration={3000}
          onClose={() => setNotificationSnackbar({ ...notificationSnackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setNotificationSnackbar({ ...notificationSnackbar, open: false })}
            severity={notificationSnackbar.severity}
            variant="filled"
            sx={{ borderRadius: '12px' }}
          >
            {notificationSnackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </LayoutKasir>
  );
};

export default Notification;